import { Input, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Checkmark20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ConversationContextMenu } from '../Common/ConversationContextMenu';
import SyButton from '../Common/SyButton';

interface ConversationHeaderMenuProps {
    conversationId: string;
    conversationTitle: string;
    isPinned: boolean;
    onTitleChange: (id: string, title: string) => void;
    onRemove: (id: string) => void;
    onPinToggle: (id: string, pinned: boolean) => void;
}

const useStyles = makeStyles({
    editContainer: {
        position: 'absolute',
        minWidth: '300px',
        backgroundColor: 'var(--color-neutral-50)',
        ...shorthands.borderRadius('12px'),
        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        zIndex: 9999,
        '& input': {
            color: 'var(--color-gray-900) !important',
        },
    },
    editActions: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end',
    },
});

export const ConversationHeaderMenu: FC<ConversationHeaderMenuProps> = (props) => {
    const { conversationId, conversationTitle, isPinned, onTitleChange, onRemove, onPinToggle } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    const [showMenu, setShowMenu] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [localTitle, setLocalTitle] = useState(conversationTitle);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const editContainerRef = useRef<HTMLDivElement>(null);
    const titleInputRef = useRef<HTMLInputElement>(null);
    // minimal: no persistent rect ref

    useEffect(() => {
        setLocalTitle(conversationTitle);
    }, [conversationTitle]);

    useEffect(() => {
        if (editMode && titleInputRef.current) {
            titleInputRef.current.focus();
            titleInputRef.current.select();
        }
    }, [conversationTitle]);

    // Close on click outside when in edit mode
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (editMode && editContainerRef.current && !editContainerRef.current.contains(event.target as Node)) {
                if (localTitle.trim().length > 0) {
                    handleConfirmEdit();
                } else {
                    handleCancelEdit();
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [editMode, localTitle, conversationTitle]);

    const handleEdit = () => {
        setShowMenu(false);
        const rect = buttonRef.current?.getBoundingClientRect() ?? null;
        if (rect) {
            // minimal: align right edge to the edit popup (minWidth = 300)
                const left = Math.max(8, rect.right - 300 - 24);
            setMenuPos({ top: rect.bottom + 8, left });
        }
        setEditMode(true);
    };

    const handleConfirmEdit = useCallback(() => {
        if (localTitle.trim().length > 0) {
            onTitleChange(conversationId, localTitle.trim());
        }
        setEditMode(false);
    }, [onTitleChange, localTitle, conversationId]);

    const handleCancelEdit = () => {
        setLocalTitle(conversationTitle);
        setEditMode(false);
    };

    return (
        <>
            <SyButton
                ref={buttonRef}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    const rect = buttonRef.current?.getBoundingClientRect();
                    if (rect) {
                        // minimal: align right edge to menu (menu min width = 220)
                            const left = Math.max(8, rect.right - 220 - 24);
                        setMenuPos({ top: rect.bottom + 8, left });
                    }
                    setShowMenu((s) => !s);
                }}
                appearance="tertiary"
                iconOnly
                className="sy-conversation-menu-btn"
                aria-label={t('More options') || 'More options'}
                icon={
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="4" cy="10" r="1.5" fill="currentColor" />
                        <circle cx="10" cy="10" r="1.5" fill="currentColor" />
                        <circle cx="16" cy="10" r="1.5" fill="currentColor" />
                    </svg>
                }
            />

            {showMenu && (
                <ConversationContextMenu
                    conversationId={conversationId}
                    isPinned={isPinned}
                    position={menuPos}
                    onEdit={handleEdit}
                    onPinToggle={onPinToggle}
                    onDelete={onRemove}
                    onClose={() => setShowMenu(false)}
                    triggerRef={buttonRef}
                />
            )}

            {editMode && createPortal(
                <div
                    ref={editContainerRef}
                    className={classes.editContainer}
                    style={{
                        position: 'fixed',
                        top: menuPos.top,
                        left: menuPos.left,
                    }}
                >
                    <Input
                        ref={titleInputRef}
                        value={localTitle}
                        onChange={(e) => setLocalTitle(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleConfirmEdit();
                            } else if (e.key === 'Escape') {
                                handleCancelEdit();
                            }
                        }}
                    />
                    <div className={classes.editActions}>
                        <SyButton
                            appearance="secondary"
                            icon={<Dismiss20Regular />}
                            onClick={handleCancelEdit}
                        />
                        <SyButton
                            appearance="primary"
                            icon={<Checkmark20Regular />}
                            onClick={handleConfirmEdit}
                            overrideBackColorLight="var(--color-primary-500)"
                            overrideBackColorDark="var(--color-primary-500)"
                            overrideForegroundColorLight="white"
                            overrideForegroundColorDark="white"
                        />
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};
